"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TerminalFlowSyncResponse = exports.TerminalFlowSnapshot = exports.SaveTerminalFlow = exports.CreateTerminalFlow = exports.TerminalFlowAdmin = exports.FlowStatus = exports.FlowRuleset = exports.FlowRule = exports.FlowConditionKind = exports.FlowCondition = exports.FlowAction = exports.FlowTrigger = exports.FlowSurface = void 0;
const zod_1 = require("zod");
/**
 * Terminal flow rules (RFL-131).
 *
 * Stores configure FSM-level terminal behaviour without code changes:
 * a per-surface (optionally per-location) ruleset of
 * `{ trigger, condition, action }` rows. The relevant terminal FSM
 * points consult the loaded ruleset instead of hardcoded `if`s
 * (e.g. "on SCO, voiding a line requires attendant approval; on POS it
 * doesn't" — the F4 pilot request).
 *
 * The same ruleset drives two Backoffice editing surfaces: a graphical
 * node-RED-style canvas (trigger → condition → action wired nodes) and a
 * declarative table / JSON view. Both edit the `rules` array below.
 *
 * Synced to terminals via the `sync.flow-rules` snapshot domain. Adding
 * triggers/actions here is a deliberate, pin-bumping wire-shape event for
 * the terminals repo (see docs/cross-repo/README.md).
 */
// ---------- Vocabulary ----------
exports.FlowSurface = zod_1.z.enum(['pos', 'sco', 'attendant']);
/**
 * FSM hook points the terminal exposes. Extend as new hookable points
 * land terminal-side (each addition is a pin-bumping event).
 */
exports.FlowTrigger = zod_1.z.enum([
    'pos.line.void',
    'pos.discount.applied',
    'pos.return.line',
    'pos.no_sale',
    'sco.line.void',
    'sco.unknown_barcode',
    'sco.age_required',
    'sco.weight_mismatch',
    'sco.manual_price',
]);
exports.FlowAction = zod_1.z.enum([
    'require_attendant_approval',
    'require_supervisor_pin',
    'log_only',
    'block',
    'prompt_customer',
]);
/**
 * Condition guarding whether the action fires. Discriminated on `kind`
 * so terminals evaluate without guessing param shape.
 */
exports.FlowCondition = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({ kind: zod_1.z.literal('always') }),
    zod_1.z.object({ kind: zod_1.z.literal('amount_over'), amountMinor: zod_1.z.number().int().min(0) }),
    zod_1.z.object({ kind: zod_1.z.literal('cashier_role_not'), role: zod_1.z.string().min(1).max(64) }),
    zod_1.z.object({
        kind: zod_1.z.literal('time_of_day_in'),
        // 24h HH:MM local-time window; wraps past midnight if from > to.
        from: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        to: zod_1.z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    }),
]);
exports.FlowConditionKind = zod_1.z.enum([
    'always',
    'amount_over',
    'cashier_role_not',
    'time_of_day_in',
]);
// ---------- Rule ----------
/**
 * A single rule. `id` is client-generated (stable across canvas/JSON
 * edits so the graph keeps node identity). `order` sets evaluation
 * precedence; the first enabled matching rule for a trigger wins.
 */
exports.FlowRule = zod_1.z.object({
    id: zod_1.z.string().min(1).max(64),
    trigger: exports.FlowTrigger,
    condition: exports.FlowCondition,
    action: exports.FlowAction,
    enabled: zod_1.z.boolean().default(true),
    order: zod_1.z.number().int().min(0),
    note: zod_1.z.string().max(500).optional(),
});
/** The editable ruleset payload (the "graph"). */
exports.FlowRuleset = zod_1.z.object({
    rules: zod_1.z.array(exports.FlowRule).max(500),
});
exports.FlowStatus = zod_1.z.enum(['draft', 'published']);
// ---------- Admin REST shapes ----------
exports.TerminalFlowAdmin = zod_1.z.object({
    id: zod_1.z.string(),
    surface: exports.FlowSurface,
    locationId: zod_1.z.string().nullable(),
    name: zod_1.z.string().min(1).max(200),
    status: exports.FlowStatus,
    version: zod_1.z.number().int(),
    rules: zod_1.z.array(exports.FlowRule),
    publishedAt: zod_1.z.string().datetime().nullable(),
    createdAt: zod_1.z.string().datetime(),
    updatedAt: zod_1.z.string().datetime(),
});
exports.CreateTerminalFlow = zod_1.z.object({
    surface: exports.FlowSurface,
    name: zod_1.z.string().min(1).max(200),
    locationId: zod_1.z.string().nullable().default(null),
    rules: zod_1.z.array(exports.FlowRule).max(500).default([]),
});
/** Save the working draft. Optimistic concurrency via expectedVersion. */
exports.SaveTerminalFlow = zod_1.z.object({
    expectedVersion: zod_1.z.number().int().min(0),
    name: zod_1.z.string().min(1).max(200).optional(),
    rules: zod_1.z.array(exports.FlowRule).max(500),
});
// ---------- Terminal sync download ----------
/** Published ruleset as terminals consume it (no audit timestamps). */
exports.TerminalFlowSnapshot = zod_1.z.object({
    id: zod_1.z.string(),
    surface: exports.FlowSurface,
    locationId: zod_1.z.string().nullable(),
    name: zod_1.z.string(),
    version: zod_1.z.number().int(),
    rules: zod_1.z.array(exports.FlowRule),
});
exports.TerminalFlowSyncResponse = zod_1.z.object({
    flows: zod_1.z.array(exports.TerminalFlowSnapshot),
    serverTime: zod_1.z.string().datetime(),
});
//# sourceMappingURL=terminal-flow.js.map